import React from "react";
import { Chip, Tooltip, Typography } from "@mui/material";
import {
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { ActionLogInfo } from "@services/admin/types";

interface StatusCount {
  errors: number;
  warnings: number;
  infos: number;
}

interface StatusMessage {
  type: "error" | "warning" | "info";
  message: string;
}

interface ActionLogStatusIndicatorProps {
  log: ActionLogInfo;
  logs?: ActionLogInfo[];
  onClick?: () => void;
}

const extractMessages = (log: ActionLogInfo): StatusMessage[] => {
  const messages: StatusMessage[] = [];

  if (log.extra?.errors) {
    if (Array.isArray(log.extra.errors)) {
      log.extra.errors.forEach((error: any) => {
        const message =
          typeof error === "string" ? error : error?.message || "Unknown error";
        messages.push({ type: "error", message });
      });
    } else {
      const message =
        typeof log.extra.errors === "string"
          ? log.extra.errors
          : log.extra.errors?.message || "Unknown error";
      messages.push({ type: "error", message });
    }
  }

  if (log.extra?.error) {
    const message =
      typeof log.extra.error === "string"
        ? log.extra.error
        : log.extra.error?.message || "Unknown error";
    messages.push({ type: "error", message });
  }

  if ((log as any).error) {
    const message =
      typeof (log as any).error === "string"
        ? (log as any).error
        : (log as any).error?.message || "Unknown error";
    messages.push({ type: "error", message });
  }

  if (log.extra?.warnings) {
    if (Array.isArray(log.extra.warnings)) {
      log.extra.warnings.forEach((warning: any) => {
        const message =
          typeof warning === "string"
            ? warning
            : warning?.message || "Unknown warning";
        messages.push({ type: "warning", message });
      });
    } else {
      const message =
        typeof log.extra.warnings === "string"
          ? log.extra.warnings
          : log.extra.warnings?.message || "Unknown warning";
      messages.push({ type: "warning", message });
    }
  }

  if (log.extra?.warning) {
    if (Array.isArray(log.extra.warning)) {
      log.extra.warning.forEach((warning: any) => {
        const message =
          typeof warning === "string"
            ? warning
            : warning?.message || "Unknown warning";
        messages.push({ type: "warning", message });
      });
    } else {
      const message =
        typeof log.extra.warning === "string"
          ? log.extra.warning
          : log.extra.warning?.message || "Unknown warning";
      messages.push({ type: "warning", message });
    }
  }

  if (log.extra?.infos) {
    if (Array.isArray(log.extra.infos)) {
      log.extra.infos.forEach((info: any) => {
        const message =
          typeof info === "string" ? info : info?.message || "Unknown info";
        messages.push({ type: "info", message });
      });
    } else {
      const message =
        typeof log.extra.infos === "string"
          ? log.extra.infos
          : log.extra.infos?.message || "Unknown info";
      messages.push({ type: "info", message });
    }
  }

  if (log.extra?.info) {
    if (Array.isArray(log.extra.info)) {
      log.extra.info.forEach((info: any) => {
        const message =
          typeof info === "string" ? info : info?.message || "Unknown info";
        messages.push({ type: "info", message });
      });
    } else {
      const message =
        typeof log.extra.info === "string"
          ? log.extra.info
          : log.extra.info?.message || "Unknown info";
      messages.push({ type: "info", message });
    }
  }

  return messages;
};

const getStatusCounts = (logs: ActionLogInfo[]): StatusCount => {
  let errors = 0;
  let warnings = 0;
  let infos = 0;

  logs.forEach((log) => {
    const messages = extractMessages(log);
    errors += messages.filter((m) => m.type === "error").length;
    warnings += messages.filter((m) => m.type === "warning").length;
    infos += messages.filter((m) => m.type === "info").length;
  });

  return { errors, warnings, infos };
};

const truncateMessage = (message: string, maxLength: number = 50): string => {
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength - 3) + "...";
};

const formatTooltipContent = (messages: StatusMessage[]): string => {
  if (messages.length === 0) return "";

  const preview = messages
    .slice(0, 3)
    .map((m) => `${truncateMessage(m.message)}`)
    .join("\n");

  if (messages.length > 3) {
    return `${preview}\n... and ${messages.length - 3} more`;
  }

  return preview;
};

export function ActionLogFullErrorDisplay({
  log,
  logs,
}: {
  log: ActionLogInfo;
  logs?: ActionLogInfo[];
}) {
  const allLogs = logs || [log];
  const allMessages = allLogs.flatMap(extractMessages);

  if (allMessages.length === 0) {
    return null;
  }

  const errors = allMessages.filter((m) => m.type === "error");
  const warnings = allMessages.filter((m) => m.type === "warning");
  const infos = allMessages.filter((m) => m.type === "info");

  return (
    <div style={{ paddingLeft: 16, display: "flex", flexDirection: "column", gap: 8 }}>
      {errors.map((error, index) => (
        <Chip
          key={`error-${index}`}
          label={error.message}
          size="small"
          color="error"
          icon={<ErrorIcon />}
          sx={{ alignSelf: "flex-start" }}
        />
      ))}
      {warnings.map((warning, index) => (
        <Chip
          key={`warning-${index}`}
          label={warning.message}
          size="small"
          color="warning"
          icon={<WarningIcon />}
          sx={{ alignSelf: "flex-start" }}
        />
      ))}
      {infos.map((info, index) => (
        <Chip
          key={`info-${index}`}
          label={info.message}
          size="small"
          color="info"
          icon={<InfoIcon />}
          sx={{ alignSelf: "flex-start" }}
        />
      ))}
    </div>
  );
}

export function ActionLogErrorIndicator({
  log,
  logs,
  onClick,
}: ActionLogStatusIndicatorProps) {
  const allLogs = logs || [log];
  const statusCounts = getStatusCounts(allLogs);
  const allMessages = allLogs.flatMap(extractMessages);

  const hasAnyStatus =
    statusCounts.errors > 0 ||
    statusCounts.warnings > 0 ||
    statusCounts.infos > 0;

  if (!hasAnyStatus) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const tooltipContent = formatTooltipContent(allMessages);

  if (statusCounts.errors > 0) {
    return (
      <Tooltip title={tooltipContent} arrow>
        <Chip
          label={statusCounts.errors}
          size="small"
          color="error"
          icon={<ErrorIcon />}
          onClick={onClick}
          sx={{ cursor: onClick ? "pointer" : "default" }}
        />
      </Tooltip>
    );
  }

  if (statusCounts.warnings > 0) {
    return (
      <Tooltip title={tooltipContent} arrow>
        <Chip
          label={statusCounts.warnings}
          size="small"
          color="warning"
          icon={<WarningIcon />}
          onClick={onClick}
          sx={{ cursor: onClick ? "pointer" : "default" }}
        />
      </Tooltip>
    );
  }

  if (statusCounts.infos > 0) {
    return (
      <Tooltip title={tooltipContent} arrow>
        <Chip
          label={statusCounts.infos}
          size="small"
          color="info"
          icon={<InfoIcon />}
          onClick={onClick}
          sx={{ cursor: onClick ? "pointer" : "default" }}
        />
      </Tooltip>
    );
  }

  return null;
}
